import { useState, useMemo } from "react";
import { Check, Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { generateEJSOutput } from "@/lib/ejsGenerator";

export function CodeExportDialog({
  open,
  onOpenChange,
  sections,
  pageSettings,
}) {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("full");

  const output = useMemo(
    () => generateEJSOutput(sections, pageSettings),
    [sections, pageSettings]
  );

  const codeMap = {
    full: output.html,
    body: output.bodyHtml,
    css: output.css,
  };

  const handleCopy = async () => {
    const text = codeMap[activeTab] || output.html;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([output.html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "invoice-template.ejs";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-lg">
            Export EJS Template
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between mb-3">
            <TabsList className="h-8">
              <TabsTrigger
                value="full"
                className="text-xs"
                data-testid="code-tab-full"
              >
                Full HTML
              </TabsTrigger>
              <TabsTrigger
                value="body"
                className="text-xs"
                data-testid="code-tab-body"
              >
                Body Only
              </TabsTrigger>
              <TabsTrigger
                value="css"
                className="text-xs"
                data-testid="code-tab-css"
              >
                CSS Only
              </TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                data-testid="copy-code-btn"
                onClick={handleCopy}
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3 text-green-500" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" /> Copy
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                data-testid="download-ejs-btn"
                onClick={handleDownload}
              >
                <Download className="w-3 h-3" /> Download .ejs
              </Button>
            </div>
          </div>

          {["full", "body", "css"].map((tab) => (
            <TabsContent
              key={tab}
              value={tab}
              className="flex-1 overflow-hidden mt-0"
            >
              <div className="h-full overflow-auto rounded-lg border border-gray-200 bg-gray-50">
                <pre className="code-block p-4 text-gray-800 text-[11px] leading-relaxed">
                  {codeMap[tab]}
                </pre>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
